<template>
  <v-card>
    <v-card-text>
      <!-- Date Range Selection -->
      <v-row>
        <v-col cols="12" md="6">
          <v-select
            outlined
            :items="dateRangeOptions"
            label="Date Range"
            v-model="datesSelection"
            hide-details
            style="margin-bottom: 10px;"
          ></v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-btn-toggle v-model="viewMode" mandatory color="primary" dense>
            <v-btn value="summary" small>Summary</v-btn>
            <v-btn value="charts" small>Charts</v-btn>
            <v-btn value="detailed" small>Detailed</v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <v-row v-if="!all" style="margin-bottom: 10px;">
        <v-col style="padding-bottom: 0px; padding-right: 6px;">
          <v-dialog
            v-model="fromDialog"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                outlined
                :value="computedFrom"
                label="From"
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                hide-details
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="from"
              @input="fromDialog = false"
              :allowed-dates="allowedFromDates"
            ></v-date-picker>
          </v-dialog>
        </v-col>
        <v-col style="padding-bottom: 0px; padding-left: 6px;">
          <v-dialog
            v-model="toDialog"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                outlined
                :value="computedTo"
                label="To"
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                hide-details
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="to"
              @input="toDialog = false"
              :allowed-dates="allowedToDates"
            ></v-date-picker>
          </v-dialog>
        </v-col>
      </v-row>

      <!-- Loading indicator -->
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <!-- Summary View -->
      <div v-if="viewMode === 'summary'">
        <!-- Key Metrics Cards -->
        <v-row class="mb-2">
          <v-col cols="6" md="3">
            <v-card outlined class="text-center pa-3" color="blue lighten-5">
              <div class="text-h5 font-weight-bold primary--text">
                {{ bookingReport ? bookingReport.total : 0 }}
              </div>
              <div class="caption">Total Bookings</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="text-center pa-3" color="green lighten-5">
              <div class="text-h5 font-weight-bold success--text">
                {{ passengerStats ? passengerStats.totalpassengers : 0 }}
              </div>
              <div class="caption">Total Passengers</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="text-center pa-3" color="orange lighten-5">
              <div class="text-h5 font-weight-bold warning--text">
                {{ tripReport ? tripReport.total : 0 }}
              </div>
              <div class="caption">Total Trips</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="text-center pa-3" color="purple lighten-5">
              <div class="text-h5 font-weight-bold purple--text">
                {{
                  decimalCurrency(revenueReport ? revenueReport.mvrtotal : 0)
                }}
              </div>
              <div class="caption">Total Revenue (MVR)</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Average Stats -->
        <v-card v-if="averageStats" flat outlined class="mb-2">
          <v-card-text>
            <div class="text-h6 mb-2">Averages</div>
            <v-row>
              <v-col cols="6" md="3">
                <div class="text-subtitle-2 grey--text">
                  Avg. Booking Value (MVR)
                </div>
                <div class="text-h6">
                  {{ decimalCurrency(averageStats.avgbookingmvr || 0) }}
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-subtitle-2 grey--text">
                  Avg. Booking Value (USD)
                </div>
                <div class="text-h6">
                  {{ decimalCurrency(averageStats.avgbookingusd || 0) }}
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-subtitle-2 grey--text">
                  Avg. Passengers/Booking
                </div>
                <div class="text-h6">
                  {{ (averageStats.avgpassengersperbooking || 0).toFixed(1) }}
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-subtitle-2 grey--text">Avg. Bookings/Day</div>
                <div class="text-h6">
                  {{ (averageStats.avgbookingsperday || 0).toFixed(1) }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </div>

      <!-- Charts View -->
      <div v-if="viewMode === 'charts' && !all">
        <v-row>
          <v-col cols="12" md="8">
            <v-card flat outlined class="mb-2">
              <v-card-title class="text-subtitle-1"
                >Revenue Trend (MVR)</v-card-title
              >
              <v-card-text>
                <line-chart
                  v-if="revenueChartData"
                  :chart-data="revenueChartData"
                  :options="lineChartOptions"
                  :height="200"
                ></line-chart>
                <div v-else class="text-center grey--text py-5">
                  No data available
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card flat outlined class="mb-2">
              <v-card-title class="text-subtitle-1"
                >Payment Methods</v-card-title
              >
              <v-card-text>
                <pie-chart
                  v-if="paymentChartData"
                  :chart-data="paymentChartData"
                  :options="pieChartOptions"
                  :height="200"
                ></pie-chart>
                <div v-else class="text-center grey--text py-5">
                  No data available
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-card flat outlined class="mb-2">
              <v-card-title class="text-subtitle-1"
                >Daily Bookings</v-card-title
              >
              <v-card-text>
                <bar-chart
                  v-if="bookingChartData"
                  :chart-data="bookingChartData"
                  :options="barChartOptions"
                  :height="150"
                ></bar-chart>
                <div v-else class="text-center grey--text py-5">
                  No data available
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
      <div
        v-else-if="viewMode === 'charts' && all"
        class="text-center py-5 grey--text"
      >
        Please select a specific date range to view charts.
      </div>

      <!-- Detailed View -->
      <div v-if="viewMode === 'detailed'">
        <!-- Download Button -->
        <v-row class="mb-2">
          <v-col class="text-right">
            <v-btn color="success" @click="downloadExcel" :loading="isLoading">
              <v-icon left>mdi-download</v-icon>
              Download Excel
            </v-btn>
          </v-col>
        </v-row>

        <!-- Revenue Details -->
        <v-card v-if="revenueReport" flat outlined class="mb-2" ripple>
          <v-card-text>
            <v-row justify="center" align="center">
              <v-col style="padding-top: 0px; padding-bottom: 0px;">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left text-h6">Revenue</th>
                        <th class="text-left"></th>
                        <th class="text-left"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Card</td>
                        <td>MVR</td>
                        <td>
                          {{ decimalCurrency(revenueReport.mvrtransaction) }}
                        </td>
                      </tr>
                      <tr>
                        <td>Card</td>
                        <td>USD</td>
                        <td>
                          {{ decimalCurrency(revenueReport.usdtransaction) }}
                        </td>
                      </tr>
                      <tr>
                        <td>Transfer</td>
                        <td>MVR</td>
                        <td>
                          {{ decimalCurrency(revenueReport.mvrtransfer) }}
                        </td>
                      </tr>
                      <tr>
                        <td>Transfer</td>
                        <td>USD</td>
                        <td>
                          {{ decimalCurrency(revenueReport.usdtransfer) }}
                        </td>
                      </tr>
                      <tr>
                        <td>Cash</td>
                        <td>MVR</td>
                        <td>{{ decimalCurrency(revenueReport.mvrcash) }}</td>
                      </tr>
                      <tr>
                        <td>Cash</td>
                        <td>USD</td>
                        <td>{{ decimalCurrency(revenueReport.usdcash) }}</td>
                      </tr>
                      <tr>
                        <td>Credit</td>
                        <td>MVR</td>
                        <td>{{ decimalCurrency(revenueReport.mvrcredit) }}</td>
                      </tr>
                      <tr>
                        <td>Credit</td>
                        <td>USD</td>
                        <td>{{ decimalCurrency(revenueReport.usdcredit) }}</td>
                      </tr>
                      <tr>
                        <td>Pre-paid</td>
                        <td>MVR</td>
                        <td>{{ decimalCurrency(revenueReport.mvrprepaid) }}</td>
                      </tr>
                      <tr>
                        <td>Pre-paid</td>
                        <td>USD</td>
                        <td>{{ decimalCurrency(revenueReport.usdprepaid) }}</td>
                      </tr>
                      <tr class="font-weight-bold">
                        <td>Total</td>
                        <td>MVR</td>
                        <td>{{ decimalCurrency(revenueReport.mvrtotal) }}</td>
                      </tr>
                      <tr class="font-weight-bold">
                        <td>Total</td>
                        <td>USD</td>
                        <td>{{ decimalCurrency(revenueReport.usdtotal) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Trip Details -->
        <v-card v-if="tripReport" flat outlined class="mb-2" ripple>
          <v-card-text>
            <v-row justify="center" align="center">
              <v-col style="padding-top: 0px; padding-bottom: 0px;">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left text-h6">Trips</th>
                        <th class="text-left"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Not departed</td>
                        <td>{{ tripReport.notdeparted }}</td>
                      </tr>
                      <tr>
                        <td>In transit</td>
                        <td>{{ tripReport.departed }}</td>
                      </tr>
                      <tr>
                        <td>Completed</td>
                        <td>{{ tripReport.ended }}</td>
                      </tr>
                      <tr class="font-weight-bold">
                        <td>Total</td>
                        <td>{{ tripReport.total }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Booking Details -->
        <v-card v-if="bookingReport" flat outlined class="mb-2" ripple>
          <v-card-text>
            <v-row justify="center" align="center">
              <v-col style="padding-top: 0px; padding-bottom: 0px;">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left text-h6">Bookings</th>
                        <th class="text-left"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Paid</td>
                        <td>{{ bookingReport.totalpaid }}</td>
                      </tr>
                      <tr>
                        <td>
                          <span style="margin-left: 10px;"></span>- Paid in MVR
                        </td>
                        <td>{{ bookingReport.paidmvr }}</td>
                      </tr>
                      <tr>
                        <td>
                          <span style="margin-left: 10px;"></span>- Paid in USD
                        </td>
                        <td>{{ bookingReport.paidusd }}</td>
                      </tr>
                      <tr>
                        <td>Allowed</td>
                        <td>{{ bookingReport.allowed }}</td>
                      </tr>
                      <tr>
                        <td>Pending</td>
                        <td>{{ bookingReport.pending }}</td>
                      </tr>
                      <tr>
                        <td>Cancelled</td>
                        <td>{{ bookingReport.cancelled }}</td>
                      </tr>
                      <tr class="font-weight-bold">
                        <td>Total</td>
                        <td>{{ bookingReport.total }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Passenger Details -->
        <v-card v-if="passengerStats" flat outlined class="mb-2" ripple>
          <v-card-text>
            <v-row justify="center" align="center">
              <v-col style="padding-top: 0px; padding-bottom: 0px;">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left text-h6">Passengers</th>
                        <th class="text-left"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Adults</td>
                        <td>{{ passengerStats.adults }}</td>
                      </tr>
                      <tr>
                        <td>Children</td>
                        <td>{{ passengerStats.children }}</td>
                      </tr>
                      <tr>
                        <td>Infants</td>
                        <td>{{ passengerStats.infants }}</td>
                      </tr>
                      <tr>
                        <td>Local Passengers</td>
                        <td>{{ passengerStats.localpassengers }}</td>
                      </tr>
                      <tr>
                        <td>Foreign Passengers</td>
                        <td>{{ passengerStats.foreignpassengers }}</td>
                      </tr>
                      <tr class="font-weight-bold">
                        <td>Total</td>
                        <td>{{ passengerStats.totalpassengers }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Top Routes -->
        <v-card
          v-if="routeStats && routeStats.length > 0"
          flat
          outlined
          class="mb-2"
        >
          <v-card-text>
            <div class="text-h6 mb-2">Top Routes</div>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">From</th>
                    <th class="text-left">To</th>
                    <th class="text-left">Bookings</th>
                    <th class="text-left">Revenue (MVR)</th>
                    <th class="text-left">Revenue (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="route in routeStats"
                    :key="`${route.fromlocation}-${route.tolocation}`"
                  >
                    <td>{{ route.fromlocation }}</td>
                    <td>{{ route.tolocation }}</td>
                    <td>{{ route.bookingcount }}</td>
                    <td>{{ decimalCurrency(route.revenuemvr || 0) }}</td>
                    <td>{{ decimalCurrency(route.revenueusd || 0) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { Bar, Line, Pie } from "vue-chartjs";
import * as XLSX from "xlsx";

const LineChart = {
  extends: Line,
  props: ["chartData", "options"],
  mounted() {
    if (this.chartData) {
      this.renderChart(this.chartData, this.options);
    }
  },
  watch: {
    chartData() {
      if (this.chartData) {
        this.renderChart(this.chartData, this.options);
      }
    },
  },
};

const PieChart = {
  extends: Pie,
  props: ["chartData", "options"],
  mounted() {
    if (this.chartData) {
      this.renderChart(this.chartData, this.options);
    }
  },
  watch: {
    chartData() {
      if (this.chartData) {
        this.renderChart(this.chartData, this.options);
      }
    },
  },
};

const BarChart = {
  extends: Bar,
  props: ["chartData", "options"],
  mounted() {
    if (this.chartData) {
      this.renderChart(this.chartData, this.options);
    }
  },
  watch: {
    chartData() {
      if (this.chartData) {
        this.renderChart(this.chartData, this.options);
      }
    },
  },
};

export default {
  name: "Reports",
  components: {
    LineChart,
    PieChart,
    BarChart,
  },
  props: {
    message: Function,
    errorMessage: Function,
    decimalCurrency: Function,
    user: Object,
  },
  data: () => ({
    viewMode: "summary",
    dateRangeOptions: [
      "Today",
      "Yesterday",
      "Last 7 Days",
      "Last 30 Days",
      "This Week",
      "Last Week",
      "Current Month",
      "Last Month",
      "This Year",
      "Last Year",
      "Year to Date",
      "Custom Range",
      "All",
    ],
    datesSelection: "Current Month",
    all: false,
    fromDialog: null,
    from: moment()
      .startOf("month")
      .format("YYYY-MM-DD"),
    toDialog: null,
    to: moment()
      .endOf("month")
      .format("YYYY-MM-DD"),
    tripReportLoading: false,
    tripReport: null,
    bookingReportLoading: false,
    bookingReport: null,
    revenueReportLoading: false,
    revenueReport: null,
    passengerStatsLoading: false,
    passengerStats: null,
    trendDataLoading: false,
    trendData: null,
    routeStatsLoading: false,
    routeStats: null,
    averageStatsLoading: false,
    averageStats: null,
    lineChartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      scales: {
        yAxes: [{ ticks: { beginAtZero: true } }],
        xAxes: [{ gridLines: { display: false } }],
      },
    },
    pieChartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      legend: { position: "bottom" },
    },
    barChartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      legend: { position: "top" },
      scales: {
        yAxes: [{ ticks: { beginAtZero: true } }],
        xAxes: [{ gridLines: { display: false } }],
      },
    },
  }),
  computed: {
    computedFrom() {
      return this.from ? moment(this.from).format("DD MMMM YYYY") : "";
    },
    computedTo() {
      return this.to ? moment(this.to).format("DD MMMM YYYY") : "";
    },
    allFromTo() {
      return `${this.all ? "true" : "false"}${this.from}${this.to}`;
    },
    isLoading() {
      return (
        this.revenueReportLoading ||
        this.tripReportLoading ||
        this.bookingReportLoading ||
        this.passengerStatsLoading ||
        this.trendDataLoading ||
        this.routeStatsLoading ||
        this.averageStatsLoading
      );
    },
    revenueChartData() {
      if (
        !this.trendData ||
        !this.trendData.revenueTrends ||
        this.trendData.revenueTrends.length === 0
      ) {
        return null;
      }
      return {
        labels: this.trendData.revenueTrends.map((d) =>
          moment(d.date).format("MMM D"),
        ),
        datasets: [
          {
            label: "Revenue (MVR)",
            data: this.trendData.revenueTrends.map((d) => d.mvr / 100),
            borderColor: "#1976D2",
            backgroundColor: "rgba(25, 118, 210, 0.1)",
            fill: true,
            tension: 0.4,
          },
        ],
      };
    },
    bookingChartData() {
      if (
        !this.trendData ||
        !this.trendData.bookingTrends ||
        this.trendData.bookingTrends.length === 0
      ) {
        return null;
      }
      return {
        labels: this.trendData.bookingTrends.map((d) =>
          moment(d.date).format("MMM D"),
        ),
        datasets: [
          {
            label: "Total",
            data: this.trendData.bookingTrends.map((d) => d.total),
            backgroundColor: "#1976D2",
          },
          {
            label: "Paid",
            data: this.trendData.bookingTrends.map((d) => d.paid),
            backgroundColor: "#4CAF50",
          },
          {
            label: "Cancelled",
            data: this.trendData.bookingTrends.map((d) => d.cancelled),
            backgroundColor: "#F44336",
          },
        ],
      };
    },
    paymentChartData() {
      if (
        !this.trendData ||
        !this.trendData.paymentBreakdown ||
        this.trendData.paymentBreakdown.length === 0
      ) {
        return null;
      }
      const breakdown = this.trendData.paymentBreakdown[0];
      if (!breakdown) return null;
      const data = [
        breakdown.card || 0,
        breakdown.transfer || 0,
        breakdown.cash || 0,
        breakdown.credit || 0,
        breakdown.prepaid || 0,
      ];
      if (data.every((v) => v === 0)) return null;
      return {
        labels: ["Card", "Transfer", "Cash", "Credit", "Pre-paid"],
        datasets: [
          {
            data: data,
            backgroundColor: [
              "#1976D2",
              "#4CAF50",
              "#FF9800",
              "#9C27B0",
              "#00BCD4",
            ],
          },
        ],
      };
    },
  },
  methods: {
    allowedFromDates(date) {
      return moment(date, "YYYY-MM-DD").isSameOrBefore(moment(this.to), "day");
    },
    allowedToDates(date) {
      return moment(date, "YYYY-MM-DD").isSameOrAfter(moment(this.from), "day");
    },
    allReports() {
      this.tripReports();
      this.bookingReports();
      this.revenueReports();
      this.fetchPassengerStats();
      this.fetchRouteStats();
      this.fetchAverageStats();
      if (!this.all) {
        this.fetchTrendData();
      }
    },
    tripReports() {
      this.tripReportLoading = true;
      const fromISO = moment(this.from).toISOString();
      const toISO = moment(this.to).toISOString();
      const query = this.all ? "?all=true" : `?from=${fromISO}&to=${toISO}`;
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/tripreports${query}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.tripReport = resp.data.report;
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.tripReportLoading = false;
        });
    },
    bookingReports() {
      this.bookingReportLoading = true;
      const fromISO = moment(this.from).toISOString();
      const toISO = moment(this.to).toISOString();
      const query = this.all ? "?all=true" : `?from=${fromISO}&to=${toISO}`;
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/bookingreports${query}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.bookingReport = resp.data.report;
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.bookingReportLoading = false;
        });
    },
    revenueReports() {
      this.revenueReportLoading = true;
      const fromISO = moment(this.from).toISOString();
      const toISO = moment(this.to)
        .endOf("day")
        .toISOString();
      const query = this.all ? "?all=true" : `?from=${fromISO}&to=${toISO}`;
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/revenuereports${query}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.revenueReport = resp.data.report;
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.revenueReportLoading = false;
        });
    },
    fetchPassengerStats() {
      this.passengerStatsLoading = true;
      const fromISO = moment(this.from).toISOString();
      const toISO = moment(this.to)
        .endOf("day")
        .toISOString();
      const query = this.all ? "?all=true" : `?from=${fromISO}&to=${toISO}`;
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/passengerstats${query}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.passengerStats = resp.data.report;
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.passengerStatsLoading = false;
        });
    },
    fetchTrendData() {
      if (this.all) return;
      this.trendDataLoading = true;
      const fromISO = moment(this.from).toISOString();
      const toISO = moment(this.to)
        .endOf("day")
        .toISOString();
      const query = `?from=${fromISO}&to=${toISO}`;
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/trenddata${query}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.trendData = resp.data;
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.trendDataLoading = false;
        });
    },
    fetchRouteStats() {
      this.routeStatsLoading = true;
      const fromISO = moment(this.from).toISOString();
      const toISO = moment(this.to)
        .endOf("day")
        .toISOString();
      const query = this.all ? "?all=true" : `?from=${fromISO}&to=${toISO}`;
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/routestats${query}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.routeStats = resp.data.routes;
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.routeStatsLoading = false;
        });
    },
    fetchAverageStats() {
      this.averageStatsLoading = true;
      const fromISO = moment(this.from).toISOString();
      const toISO = moment(this.to)
        .endOf("day")
        .toISOString();
      const query = this.all ? "?all=true" : `?from=${fromISO}&to=${toISO}`;
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/averagestats${query}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.averageStats = resp.data.report;
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.averageStatsLoading = false;
        });
    },
    isPresetMatch() {
      const presetDates = this.getPresetDates(this.datesSelection);
      if (!presetDates) return false;
      return this.from === presetDates.from && this.to === presetDates.to;
    },
    getPresetDates(preset) {
      switch (preset) {
        case "Today":
          return {
            from: moment().format("YYYY-MM-DD"),
            to: moment().format("YYYY-MM-DD"),
          };
        case "Yesterday":
          return {
            from: moment()
              .subtract(1, "day")
              .format("YYYY-MM-DD"),
            to: moment()
              .subtract(1, "day")
              .format("YYYY-MM-DD"),
          };
        case "Last 7 Days":
          return {
            from: moment()
              .subtract(6, "days")
              .format("YYYY-MM-DD"),
            to: moment().format("YYYY-MM-DD"),
          };
        case "Last 30 Days":
          return {
            from: moment()
              .subtract(29, "days")
              .format("YYYY-MM-DD"),
            to: moment().format("YYYY-MM-DD"),
          };
        case "This Week":
          return {
            from: moment()
              .startOf("week")
              .format("YYYY-MM-DD"),
            to: moment()
              .endOf("week")
              .format("YYYY-MM-DD"),
          };
        case "Last Week":
          return {
            from: moment()
              .subtract(1, "week")
              .startOf("week")
              .format("YYYY-MM-DD"),
            to: moment()
              .subtract(1, "week")
              .endOf("week")
              .format("YYYY-MM-DD"),
          };
        case "Current Month":
          return {
            from: moment()
              .startOf("month")
              .format("YYYY-MM-DD"),
            to: moment()
              .endOf("month")
              .format("YYYY-MM-DD"),
          };
        case "Last Month":
          return {
            from: moment()
              .subtract(1, "month")
              .startOf("month")
              .format("YYYY-MM-DD"),
            to: moment()
              .subtract(1, "month")
              .endOf("month")
              .format("YYYY-MM-DD"),
          };
        case "This Year":
          return {
            from: moment()
              .startOf("year")
              .format("YYYY-MM-DD"),
            to: moment()
              .endOf("year")
              .format("YYYY-MM-DD"),
          };
        case "Last Year":
          return {
            from: moment()
              .subtract(1, "year")
              .startOf("year")
              .format("YYYY-MM-DD"),
            to: moment()
              .subtract(1, "year")
              .endOf("year")
              .format("YYYY-MM-DD"),
          };
        case "Year to Date":
          return {
            from: moment()
              .startOf("year")
              .format("YYYY-MM-DD"),
            to: moment().format("YYYY-MM-DD"),
          };
        default:
          return null;
      }
    },
    downloadExcel() {
      const wb = XLSX.utils.book_new();
      const generatedAt = moment().format("DD MMM YYYY, h:mm A");
      const generatedBy = this.user ? this.user.username : "Unknown";
      const reportPeriod = this.all
        ? "All Time"
        : `${moment(this.from).format("DD MMM YYYY")} - ${moment(
            this.to,
          ).format("DD MMM YYYY")}`;

      // Report Info Sheet
      const infoData = [
        ["Report Information"],
        [""],
        ["Generated At:", generatedAt],
        ["Generated By:", generatedBy],
        ["Report Period:", reportPeriod],
        ["Date Range Selection:", this.datesSelection],
      ];
      const infoSheet = XLSX.utils.aoa_to_sheet(infoData);
      XLSX.utils.book_append_sheet(wb, infoSheet, "Report Info");

      // Revenue Sheet
      if (this.revenueReport) {
        const revenueData = [
          ["Revenue Report"],
          [""],
          ["Payment Type", "Currency", "Amount"],
          [
            "Card",
            "MVR",
            this.decimalCurrency(this.revenueReport.mvrtransaction),
          ],
          [
            "Card",
            "USD",
            this.decimalCurrency(this.revenueReport.usdtransaction),
          ],
          [
            "Transfer",
            "MVR",
            this.decimalCurrency(this.revenueReport.mvrtransfer),
          ],
          [
            "Transfer",
            "USD",
            this.decimalCurrency(this.revenueReport.usdtransfer),
          ],
          ["Cash", "MVR", this.decimalCurrency(this.revenueReport.mvrcash)],
          ["Cash", "USD", this.decimalCurrency(this.revenueReport.usdcash)],
          ["Credit", "MVR", this.decimalCurrency(this.revenueReport.mvrcredit)],
          ["Credit", "USD", this.decimalCurrency(this.revenueReport.usdcredit)],
          [
            "Pre-paid",
            "MVR",
            this.decimalCurrency(this.revenueReport.mvrprepaid),
          ],
          [
            "Pre-paid",
            "USD",
            this.decimalCurrency(this.revenueReport.usdprepaid),
          ],
          [""],
          ["Total", "MVR", this.decimalCurrency(this.revenueReport.mvrtotal)],
          ["Total", "USD", this.decimalCurrency(this.revenueReport.usdtotal)],
        ];
        const revenueSheet = XLSX.utils.aoa_to_sheet(revenueData);
        XLSX.utils.book_append_sheet(wb, revenueSheet, "Revenue");
      }

      // Trips Sheet
      if (this.tripReport) {
        const tripData = [
          ["Trips Report"],
          [""],
          ["Status", "Count"],
          ["Not Departed", this.tripReport.notdeparted],
          ["In Transit", this.tripReport.departed],
          ["Completed", this.tripReport.ended],
          [""],
          ["Total", this.tripReport.total],
        ];
        const tripSheet = XLSX.utils.aoa_to_sheet(tripData);
        XLSX.utils.book_append_sheet(wb, tripSheet, "Trips");
      }

      // Bookings Sheet
      if (this.bookingReport) {
        const bookingData = [
          ["Bookings Report"],
          [""],
          ["Status", "Count"],
          ["Paid", this.bookingReport.totalpaid],
          ["  - Paid in MVR", this.bookingReport.paidmvr],
          ["  - Paid in USD", this.bookingReport.paidusd],
          ["Allowed", this.bookingReport.allowed],
          ["Pending", this.bookingReport.pending],
          ["Cancelled", this.bookingReport.cancelled],
          [""],
          ["Total", this.bookingReport.total],
        ];
        const bookingSheet = XLSX.utils.aoa_to_sheet(bookingData);
        XLSX.utils.book_append_sheet(wb, bookingSheet, "Bookings");
      }

      // Passengers Sheet
      if (this.passengerStats) {
        const passengerData = [
          ["Passengers Report"],
          [""],
          ["Category", "Count"],
          ["Adults", this.passengerStats.adults],
          ["Children", this.passengerStats.children],
          ["Infants", this.passengerStats.infants],
          ["Local Passengers", this.passengerStats.localpassengers],
          ["Foreign Passengers", this.passengerStats.foreignpassengers],
          [""],
          ["Total", this.passengerStats.totalpassengers],
        ];
        const passengerSheet = XLSX.utils.aoa_to_sheet(passengerData);
        XLSX.utils.book_append_sheet(wb, passengerSheet, "Passengers");
      }

      // Routes Sheet
      if (this.routeStats && this.routeStats.length > 0) {
        const routeData = [
          ["Top Routes Report"],
          [""],
          ["From", "To", "Bookings", "Revenue (MVR)", "Revenue (USD)"],
          ...this.routeStats.map((route) => [
            route.fromlocation,
            route.tolocation,
            route.bookingcount,
            this.decimalCurrency(route.revenuemvr || 0),
            this.decimalCurrency(route.revenueusd || 0),
          ]),
        ];
        const routeSheet = XLSX.utils.aoa_to_sheet(routeData);
        XLSX.utils.book_append_sheet(wb, routeSheet, "Top Routes");
      }

      // Average Stats Sheet
      if (this.averageStats) {
        const avgData = [
          ["Averages Report"],
          [""],
          ["Metric", "Value"],
          [
            "Avg. Booking Value (MVR)",
            this.decimalCurrency(this.averageStats.avgbookingmvr || 0),
          ],
          [
            "Avg. Booking Value (USD)",
            this.decimalCurrency(this.averageStats.avgbookingusd || 0),
          ],
          [
            "Avg. Passengers/Booking",
            (this.averageStats.avgpassengersperbooking || 0).toFixed(1),
          ],
          [
            "Avg. Bookings/Day",
            (this.averageStats.avgbookingsperday || 0).toFixed(1),
          ],
        ];
        const avgSheet = XLSX.utils.aoa_to_sheet(avgData);
        XLSX.utils.book_append_sheet(wb, avgSheet, "Averages");
      }

      // Download the file
      const fileName = `Report_${moment().format("YYYY-MM-DD_HHmmss")}.xlsx`;
      XLSX.writeFile(wb, fileName);
      this.message("Report downloaded successfully");
    },
  },
  mounted() {
    this.allReports();
  },
  watch: {
    datesSelection: {
      handler: function(val) {
        const now = moment();
        if (val === "All") {
          this.all = true;
        } else {
          this.all = false;
        }
        switch (val) {
          case "Today":
            this.from = now.format("YYYY-MM-DD");
            this.to = now.format("YYYY-MM-DD");
            break;
          case "Yesterday":
            this.from = now.subtract(1, "day").format("YYYY-MM-DD");
            this.to = this.from;
            break;
          case "Last 7 Days":
            this.from = moment()
              .subtract(6, "days")
              .format("YYYY-MM-DD");
            this.to = moment().format("YYYY-MM-DD");
            break;
          case "Last 30 Days":
            this.from = moment()
              .subtract(29, "days")
              .format("YYYY-MM-DD");
            this.to = moment().format("YYYY-MM-DD");
            break;
          case "This Week":
            this.from = moment()
              .startOf("week")
              .format("YYYY-MM-DD");
            this.to = moment()
              .endOf("week")
              .format("YYYY-MM-DD");
            break;
          case "Last Week":
            this.from = moment()
              .subtract(1, "week")
              .startOf("week")
              .format("YYYY-MM-DD");
            this.to = moment()
              .subtract(1, "week")
              .endOf("week")
              .format("YYYY-MM-DD");
            break;
          case "Current Month":
            this.from = moment()
              .startOf("month")
              .format("YYYY-MM-DD");
            this.to = moment()
              .endOf("month")
              .format("YYYY-MM-DD");
            break;
          case "Last Month":
            this.from = moment()
              .subtract(1, "month")
              .startOf("month")
              .format("YYYY-MM-DD");
            this.to = moment()
              .subtract(1, "month")
              .endOf("month")
              .format("YYYY-MM-DD");
            break;
          case "This Year":
            this.from = moment()
              .startOf("year")
              .format("YYYY-MM-DD");
            this.to = moment()
              .endOf("year")
              .format("YYYY-MM-DD");
            break;
          case "Last Year":
            this.from = moment()
              .subtract(1, "year")
              .startOf("year")
              .format("YYYY-MM-DD");
            this.to = moment()
              .subtract(1, "year")
              .endOf("year")
              .format("YYYY-MM-DD");
            break;
          case "Year to Date":
            this.from = moment()
              .startOf("year")
              .format("YYYY-MM-DD");
            this.to = moment().format("YYYY-MM-DD");
            break;
        }
      },
    },
    allFromTo: {
      handler: function() {
        this.allReports();
      },
    },
    from: {
      handler: function() {
        if (this.datesSelection !== "Custom Range" && !this.isPresetMatch()) {
          this.datesSelection = "Custom Range";
        }
      },
    },
    to: {
      handler: function() {
        if (this.datesSelection !== "Custom Range" && !this.isPresetMatch()) {
          this.datesSelection = "Custom Range";
        }
      },
    },
  },
};
</script>
